import type { ReactNode } from 'react'

import { useT } from '@/lib/i18n'
import { inputClass, labelClass } from '@/lib/ui'
import { COUNTRIES } from '@/constants/shop'

export type CustomerForm = {
    name: string
    email: string
    phone: string
    address1: string
    address2: string
    city: string
    region: string
    postalCode: string
    country: string
}

export const emptyCustomer: CustomerForm = {
    name: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    region: '',
    postalCode: '',
    country: 'ZA'
}

interface CustomerFieldsProps {
    value: CustomerForm
    onChange: (value: CustomerForm) => void
    errorField?: string
    idPrefix: string
}

export const CustomerFields = ({ value, onChange, errorField, idPrefix }: CustomerFieldsProps): ReactNode => {
    const t = useT()

    const field = (key: keyof CustomerForm, label: string, options: { type?: string, autoComplete?: string, required?: boolean, wide?: boolean } = {}) => (
        <div className={options.wide ? 'sm:col-span-2' : undefined}>
            <label htmlFor={`${idPrefix}-${key}`} className={labelClass}>
                {label}
            </label>

            <input
                id={`${idPrefix}-${key}`}
                type={options.type ?? 'text'}
                autoComplete={options.autoComplete}
                required={options.required !== false}
                value={value[key]}
                onChange={event => onChange({ ...value, [key]: event.target.value })}
                aria-invalid={errorField === key || undefined}
                className={inputClass}
            />
        </div>
    )

    return (
        <div className='grid gap-4 sm:grid-cols-2'>
            {field('name', t('name'), { autoComplete: 'name', wide: true })}
            {field('email', t('email'), { type: 'email', autoComplete: 'email' })}
            {field('phone', t('phone'), { type: 'tel', autoComplete: 'tel' })}
            {field('address1', t('address1'), { autoComplete: 'address-line1', wide: true })}
            {field('address2', t('address2'), { autoComplete: 'address-line2', required: false, wide: true })}
            {field('city', t('city'), { autoComplete: 'address-level2' })}
            {field('region', t('region'), { autoComplete: 'address-level1', required: false })}
            {field('postalCode', t('postalCode'), { autoComplete: 'postal-code' })}

            <div>
                <label htmlFor={`${idPrefix}-country`} className={labelClass}>
                    {t('country')}
                </label>

                <select
                    id={`${idPrefix}-country`}
                    autoComplete='country'
                    value={value.country}
                    onChange={event => onChange({ ...value, country: event.target.value })}
                    className={inputClass}
                >
                    {COUNTRIES.map(country => (
                        <option key={country.code} value={country.code}>
                            {country.name}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    )
}
